import { isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { Formik } from 'formik'

import { CopyToClipboard } from '@10xLabs/react-web/components/CopyToClipboard'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { FilterIcon } from 'components/Icons/FilterIcon'
import { ResetIcon } from 'components/Icons/ResetIcon'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import IconButton from 'components/v2/IconButton'
import { Select } from 'components/v2/Select'
import {
  EventLine,
  TimelineDecoratorWrapper,
  TimelineDetailWrapper,
  TimelineEventWrapper,
  TimelineWrapper
} from 'components/v2/Timeline'
import { eventTypeMessages, getCancellationReasonLabel, getItineraryPlaceByPlaceId } from 'features/Trips/utils'
import { UserAutocomplete } from 'features/Users/components/UserAutocomplete'
import { getUserLabel } from 'features/Users/utils'
import { safeFormatMessage } from 'utils/intl'
import { ITimelineFilterValues } from 'features/Trips/types'
import { filterTimelineEvents, getTimelineDotColor, isLinkableEvent } from 'features/Trips/utils'
import { device } from 'utils/style'
import { TripV2, TripV2TimelineEvent } from 'gql/generated/graphql'

enum TimeFilterOptions {
  Today = 0,
  Yesterday = 1,
  SevenDays = 7,
  Month = 30,
  ThreeMonths = 90
}

const timelineMessages = defineMessages({
  timePlaceholder: {
    id: 'trip.drawer.timeline.time.placeholder',
    defaultMessage: 'Select a time'
  },
  usernamePlaceholder: {
    id: 'trip.drawer.timeline.username.placeholder',
    defaultMessage: 'Enter a username'
  },
  eventPlaceholder: {
    id: 'trip.drawer.timeline.event.placeholder',
    defaultMessage: 'Enter an event'
  }
})

const TripTimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FiltersWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledResetIcon = styled(ResetIcon)`
  height: 16px;
  width: 16px;
`

const StyledFilterIcon = styled(FilterIcon)`
  height: 16px;
  width: 16px;
`

const StyledIconButton = styled(IconButton)`
  padding: 12px;
`

const IconsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`

const TimelineDateTimeWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media ${device.tablet} {
    align-items: center;
    flex-direction: row;
    justify-content: flex-end;
  }
`

const TimelineItemTitleWrapper = styled.div`
  display: flex;
  gap: 4px;
`

const TimelineItemTitle = styled(Heading.Body)`
  font-weight: 500;
`

const TimelineItemLink = styled(Heading.Body)`
  display: flex;
  font-weight: 500;
  gap: 4px;
  color: #5d59d7;
  cursor: pointer;
  text-decoration: underline;
`

const TimelineItemDescription = styled(Heading.Label)`
  font-size: 14px;
  text-transform: none;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;

  p {
    margin: 0;
  }
`

interface ITripTimelineProps {
  timeline: TripV2TimelineEvent[]
  trip: TripV2
}

export const TripTimeline = ({ timeline, trip }: ITripTimelineProps): ReactElement => {
  const intl = useIntl()
  const [tripTimeline, setTripTimeline] = useState(timeline)
  const [showFilters, setShowFilters] = useState(false)
  const isMoreThanOneEvent = tripTimeline.length > 1

  const initialValues: ITimelineFilterValues = {
    time: '',
    user: {
      id: ''
    },
    event: ''
  }

  const onSubmitFilters = (values: ITimelineFilterValues): void => {
    const filteredTimeline = filterTimelineEvents(timeline, values)
    setTripTimeline(filteredTimeline)
  }

  let content = <></>
  if (isEmpty(tripTimeline)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="trips.drawer.timeline.empty.filters.description"
            defaultMessage="There were no events found according to your filters"
          />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else {
    content = (
      <TimelineWrapper>
        {tripTimeline.map((event, index): ReactElement => {
          const author = `${event.user.firstName} ${event.user.lastName}`
          const isLastEvent = index === tripTimeline.length - 1
          const shouldShowLine = !isLastEvent || !isMoreThanOneEvent
          const isLinkable = isLinkableEvent(event.type)
          const reason = getCancellationReasonLabel(intl, event.tripCancellationReason)

          return (
            <TimelineEventWrapper key={event.correlationID}>
              <TimelineDetailWrapper>
                <TimelineDateTimeWrapper>
                  <TimelineItemTitle>
                    <FormattedDate intl={intl} value={event.createdAt} month="2-digit" day="2-digit" year="numeric" />
                  </TimelineItemTitle>
                  <TimelineItemDescription>
                    <FormattedTimeByDate intl={intl} date={event.createdAt} />
                  </TimelineItemDescription>
                </TimelineDateTimeWrapper>
              </TimelineDetailWrapper>
              <TimelineDecoratorWrapper>
                <EventLine color={getTimelineDotColor(event.type)} showLine={shouldShowLine} />
              </TimelineDecoratorWrapper>
              <TimelineDetailWrapper>
                <TimelineItemTitleWrapper>
                  {isLinkable ? (
                    <>
                      <TimelineItemLink as="a" href={`/changes?id=${event.correlationID}&dateRangeValue=three_months`}>
                        {safeFormatMessage({ intl, messages: eventTypeMessages, key: event.type })}
                      </TimelineItemLink>
                      <CopyToClipboard str={event.correlationID} />
                    </>
                  ) : (
                    <TimelineItemTitle>
                      {safeFormatMessage({ intl, messages: eventTypeMessages, key: event.type })}
                    </TimelineItemTitle>
                  )}
                </TimelineItemTitleWrapper>
                <TimelineItemDescription>
                  {event.placeID && trip ? (
                    <p>
                      <FormattedMessage
                        id="trip.drawer.timeline.item.place"
                        defaultMessage="at: {placeName}"
                        values={{ placeName: getItineraryPlaceByPlaceId(trip.itinerary, event.placeID)?.place.name }}
                      />
                    </p>
                  ) : null}
                  {reason && (
                    <p>
                      <FormattedMessage
                        id="trip.drawer.timeline.item.reason"
                        defaultMessage="Reason: {reason}"
                        values={{ reason }}
                      />
                    </p>
                  )}
                  <p>
                    <FormattedMessage
                      id="trip.drawer.timeline.item.author"
                      defaultMessage="by: {author}"
                      values={{ author }}
                    />
                  </p>
                </TimelineItemDescription>
              </TimelineDetailWrapper>
            </TimelineEventWrapper>
          )
        })}
      </TimelineWrapper>
    )
  }

  return (
    <TripTimelineWrapper>
      <Formik initialValues={initialValues} onSubmit={onSubmitFilters} enableReinitialize>
        {({ values, setFieldValue, handleChange, handleSubmit, resetForm }): ReactElement => {
          return (
            <FiltersWrapper onSubmit={handleSubmit} onChange={handleSubmit}>
              <IconsWrapper>
                <StyledIconButton
                  onClick={(): void => {
                    resetForm()
                    handleSubmit()
                  }}
                  icon={<StyledResetIcon />}
                />
                <StyledIconButton onClick={(): void => setShowFilters(!showFilters)} icon={<StyledFilterIcon />} />
              </IconsWrapper>
              {showFilters && (
                <>
                  <FiltersRow>
                    <Select
                      id="time"
                      value={values.time}
                      onChange={handleChange}
                      label={<FormattedMessage id="dictionary.time" defaultMessage="Time" />}
                    >
                      <option value="">{intl.formatMessage(timelineMessages.timePlaceholder)}</option>
                      <option value={TimeFilterOptions.Today}>
                        <FormattedMessage id="dictionary.today" defaultMessage="Today" />
                      </option>
                      <option value={TimeFilterOptions.Yesterday}>
                        <FormattedMessage id="dictionary.yesterday" defaultMessage="Yesterday" />
                      </option>
                      <option value={TimeFilterOptions.SevenDays}>
                        <FormattedMessage id="trip.timeline.time.filter.week" defaultMessage="Seven days" />
                      </option>
                      <option value={TimeFilterOptions.Month}>
                        <FormattedMessage id="trip.timeline.time.filter.month" defaultMessage="This month" />
                      </option>
                      <option value={TimeFilterOptions.ThreeMonths}>
                        <FormattedMessage id="trip.timeline.time.filter.three.months" defaultMessage="Three month" />
                      </option>
                    </Select>
                    <UserAutocomplete
                      value={values.user ? getUserLabel(values.user) : ''}
                      getItemLabel={getUserLabel}
                      onChange={(user): void => {
                        setFieldValue('user', user)
                        onSubmitFilters({ ...values, user })
                      }}
                      label={<FormattedMessage id="trips.timeline.username.label" defaultMessage="Username" />}
                      placeholder={intl.formatMessage(timelineMessages.usernamePlaceholder)}
                    />
                  </FiltersRow>
                  <Select
                    id="event"
                    value={values.event}
                    onChange={handleChange}
                    label={<FormattedMessage id="dictionary.event" defaultMessage="Event" />}
                  >
                    <option value="">{intl.formatMessage(timelineMessages.eventPlaceholder)}</option>
                    {Object.entries(eventTypeMessages).map(([key, value]) => (
                      <option key={key} value={key}>
                        {intl.formatMessage(value)}
                      </option>
                    ))}
                  </Select>
                </>
              )}
            </FiltersWrapper>
          )
        }}
      </Formik>
      {content}
    </TripTimelineWrapper>
  )
}
